import React, { useEffect, useState } from 'react'
import { normalizeSettlementStatusFilter, settlementStatusOptions } from './settlement-status-filter'
import { ownPendingEmailFilters } from '../utils/own-pending-email-filters'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import PaginationBar from 'components/organisms/pagination-bar/pagination-bar'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table
} from 'reactstrap'

import ConsignorSettlementsApiV2 from 'api/v2/admins/consignor-settlements-api-v2'
import TitlePage from 'components/atoms/title-page'
import { useAuth } from 'providers/auth-provider'
import { settlementStatusNote, settlementEmailLabel } from '../utils/finance-submission-guidance'

const propTypes = {
  canNegotiate        : PropTypes.bool,
  canSync             : PropTypes.bool,
  detailBasePath      : PropTypes.string.isRequired,
  embedded            : PropTypes.bool,
  financeApproval     : PropTypes.bool,
  financeProcess      : PropTypes.bool,
  financeQueue        : PropTypes.bool,
  fixedInternalCategory: PropTypes.string,
  pageSubTitle        : PropTypes.string,
  pageTitle           : PropTypes.string,
  settlementKind      : PropTypes.oneOf([ 'consignor', 'seller', 'all' ]),
  teamLabel           : PropTypes.string
}

const defaultProps = {
  canNegotiate         : false,
  canSync              : false,
  embedded             : false,
  financeApproval      : false,
  financeProcess       : false,
  financeQueue         : false,
  fixedInternalCategory: '',
  pageSubTitle         : '',
  pageTitle            : '',
  settlementKind       : 'consignor',
  teamLabel            : ''
}

const LEGACY_STATE_LABELS = {
  draft                 : 'Draft',
  ready_for_negotiation : 'On Process',
  negotiated                  : 'On Process',
  waiting_finance_confirmation : 'Finance Approval',
  finance_requested           : 'Ready to Transfer',
  transferred_manually  : 'Transfer Done Manually',
  transferred           : 'Transfer Done',
  email_failed          : 'Email Failed',
  email_sent            : 'Email Delivered',
  cancelled             : 'Cancelled'
}

const STATE_LABELS = {
  transferred_manually: 'Transfer Done Manually',
  draft                        : 'On Process',
  ready_for_negotiation        : 'On Process',
  negotiated                   : 'On Process',
  waiting_finance_confirmation : 'Finance Approval',
  finance_requested            : 'Ready to Transfer',
  transferred                  : 'Transfer Done',
  email_failed                 : 'Email Failed',
  email_sent                   : 'Email Delivered',
  cancelled                    : 'Cancelled'
}

const stateColor = (state) => {
  if (state === 'transferred_manually') return 'success'
  if (state === 'email_sent') return 'success'
  if (state === 'email_failed') return 'danger'
  if (state === 'transferred') return 'info'
  if (state === 'waiting_finance_confirmation') return 'warning'
  if (state === 'finance_requested') return 'primary'
  if (state === 'negotiated') return 'warning'
  if (state === 'cancelled') return 'danger'

  return 'secondary'
}

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  const number = Number(value)
  if (Number.isNaN(number)) return value

  return new Intl.NumberFormat('id-ID').format(number)
}

const formatDateTime = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('id-ID', {
    timeZone: 'Asia/Jakarta'
  })
}

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(
    new Blob([ blob ], { type: 'text/csv;charset=utf-8;' })
  )
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.parentNode.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const cellStyle = {
  lineHeight    : 1.5,
  padding       : '0.75rem 0.6rem',
  overflowWrap  : 'break-word',
  verticalAlign : 'middle',
  whiteSpace    : 'normal',
  wordBreak     : 'break-word'
}

const compactCellStyle = {
  ...cellStyle,
  fontSize: '0.8125rem'
}

const singleLineCellStyle = {
  ...compactCellStyle,
  overflowWrap : 'normal',
  whiteSpace   : 'nowrap',
  wordBreak    : 'normal'
}

const normalizeSettlementNumberInput = (value) => {
  const digits = value.trim().replace(/^BNKST-/i, '')
  return /^\d{8,}$/.test(digits) ? digits.slice(0, 7) : value
}

const SettlementColumnHeader = ({ children, label, width }) => (
  <th className='font-weight-bold text-center' style={{ minWidth: width, padding: '0.75rem 0.6rem', verticalAlign: 'bottom', whiteSpace: 'normal' }}>
    <h5 className='mb-2' style={{ fontSize: '0.875rem', lineHeight: 1.4 }}>{ label }</h5>
    { children && <div>{ children }</div> }
  </th>
)

SettlementColumnHeader.propTypes = { children: PropTypes.node, label: PropTypes.node.isRequired, width: PropTypes.number.isRequired }
SettlementColumnHeader.defaultProps = { children: null }

const FINANCE_QUEUE_STATE = 'finance_requested,transferred,email_failed,email_sent'

const LIST_QUERY_KEYS = [
  'q',
  'state',
  'deal_state',
  'product_curator',
  'sourced_by',
  'settlement_number', 'internal_category', 'email_status',
  'eligible_from',
  'eligible_to',
  'deal_created_from',
  'deal_created_to',
  'requested_from',
  'requested_to'
]

const resolveInitialListState = ({ financeApproval, financeProcess, financeQueue, dealDateView }) => {
  const params = new URLSearchParams(window.location.search)
  const parsedPage = Number.parseInt(params.get('page'), 10)
  const defaultState = financeApproval
    ? 'waiting_finance_confirmation'
    : financeProcess
      ? 'finance_requested'
      : financeQueue
        ? FINANCE_QUEUE_STATE
        : ''

  return {
    page: Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage,
    filters: {
      internal_category: params.get('internal_category') || '',
      email_status: params.get('email_status') || '',
      q                : params.get('q') || params.get('sku') || params.get('reference_number') || '',
      state            : normalizeSettlementStatusFilter(params.get('state') || defaultState),
      deal_state       : params.get('deal_state') || '',
      product_curator  : params.get('product_curator') || '',
      sourced_by       : params.get('sourced_by') || '',
      settlement_number: params.get('settlement_number') || '',
      eligible_from    : dealDateView ? '' : params.get('eligible_from') || '',
      eligible_to      : dealDateView ? '' : params.get('eligible_to') || '',
      deal_created_from: params.get('deal_created_from') || (dealDateView ? params.get('eligible_from') : '') || '',
      deal_created_to  : params.get('deal_created_to') || (dealDateView ? params.get('eligible_to') : '') || '',
      requested_from   : params.get('requested_from') || '',
      requested_to     : params.get('requested_to') || ''
    }
  }
}

const ConsignorSettlementsListPage = ({
  canNegotiate,
  canSync,
  detailBasePath,
  embedded,
  financeApproval,
  financeProcess,
  financeQueue,
  fixedInternalCategory,
  pageSubTitle,
  pageTitle,
  settlementKind,
  teamLabel
}) => {
  const history = useHistory()
  const { user } = useAuth()
  const canExport = ['owner', 'admin', 'finance'].includes(String(user?.role || '').trim().toLowerCase())
  const [ initialListState ] = useState(() => resolveInitialListState({
    financeApproval,
    financeProcess,
    financeQueue,
    dealDateView: canNegotiate && ['consignor', 'seller'].includes(settlementKind)
  }))
  const [ rows, setRows ] = useState([])
  const [ selectedIds, setSelectedIds ] = useState([])
  const [ isMarkingManual, setIsMarkingManual ] = useState(false)
  const [ showManualTransfers, setShowManualTransfers ] = useState(false)
  const [ meta, setMeta ] = useState({})
  const [ filterOptions, setFilterOptions ] = useState({ states: [] })
  const [ filters, setFilters ] = useState(initialListState.filters)
  const [ page, setPage ] = useState(initialListState.page)
  const financeArea = financeApproval || financeProcess || financeQueue
  const financeTransferStatuses = { waiting_finance_confirmation: 'pending', finance_requested: 'approved', transferred: 'paid', [FINANCE_QUEUE_STATE]: 'pending,approved,paid' }
  const financeScopedView = financeArea && Boolean(financeTransferStatuses[filters.state])
  const selectedTransferStatus = financeTransferStatuses[filters.state] || undefined

  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSyncing, setIsSyncing ] = useState(false)
  const [ isExporting, setIsExporting ] = useState(false)
  const [ apiMessage, setApiMessage ] = useState('')
  const [ successMessage, setSuccessMessage ] = useState('')

  const setFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value
    }))
  }

  const applyColumnFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: value }
    setFilters(nextFilters)
    loadRows(1, nextFilters)
  }

  const applyOwnPendingEmail = () => {
    const nextFilters = ownPendingEmailFilters(initialListState.filters, filterOptions.current_admin_product_curator)
    if (!nextFilters || isLoading) return

    setFilters(nextFilters)
    loadRows(1, nextFilters)
  }

  const syncListUrl = (nextPage, nextFilters) => {
    const params = new URLSearchParams()

    params.set('page', String(nextPage))

    if (embedded && settlementKind === 'seller') params.set('tab', 'settlement')

    LIST_QUERY_KEYS.forEach((key) => {
      const value = nextFilters[key]

      if (value !== null && value !== undefined && String(value).trim() !== '') {
        params.set(key, String(value))
      }
    })

    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`
    )
  }

  const loadRows = async (nextPage = page, nextFilters = filters) => {
    const safePage = Math.max(Number(nextPage) || 1, 1)

    setPage(safePage)
    syncListUrl(safePage, nextFilters)
    setIsLoading(true)
    setSelectedIds([])
    setApiMessage('')
    setSuccessMessage('')

    try {
      const response = await ConsignorSettlementsApiV2.get({
        ...nextFilters,
        internal_category: fixedInternalCategory || nextFilters.internal_category || undefined,
        finance_transfers: financeArea && Boolean(financeTransferStatuses[nextFilters.state]) || undefined,
        transfer_status: financeTransferStatuses[nextFilters.state] || undefined,
        requested_from: financeArea && !financeTransferStatuses[nextFilters.state] ? undefined : nextFilters.requested_from,
        requested_to: financeArea && !financeTransferStatuses[nextFilters.state] ? undefined : nextFilters.requested_to,
        deal_created_from: financeArea && financeTransferStatuses[nextFilters.state] ? undefined : nextFilters.deal_created_from,
        deal_created_to: financeArea && financeTransferStatuses[nextFilters.state] ? undefined : nextFilters.deal_created_to,
        settlement_kind : settlementKind,
        page            : safePage,
        per_page : 20
      })

      setRows(response.data?.data || [])
      setMeta(response.data?.meta || {})
      setFilterOptions(response.data?.filters || { states: [] })
    }
    catch (error) {
      setRows([])
      setMeta({})
      setApiMessage(
        error?.response?.data?.message ||
        error?.response?.data?.messages?.base ||
        'Data Consignor Settlement tidak dapat dimuat.'
      )
    }
    finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRows(initialListState.page, initialListState.filters)
    // Intentional first-load only. Page + applied filters are restored from URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const manualSelectionEnabled = canNegotiate && !financeArea && showManualTransfers
  const eligibleIds = rows.filter((row) => row.can_mark_transferred_manually).map((row) => row.id)
  const toggleManualSelection = (id) => {
    setSelectedIds((previous) => previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id])
  }
  const markSelectedManually = async () => {
    const ids = selectedIds.filter((id) => eligibleIds.includes(id))
    if (!ids.length || isMarkingManual) return
    const note = window.prompt('Hanya untuk Transfer / Settlement yang sudah lunas di luar sistem, Deal sebelum 15 Oktober 2026 pukul 00.00 WIB. Isi catatan / referensi transfer manual:')
    if (!note || !note.trim()) return
    if (!window.confirm(`Tandai ${ids.length} settlement sebagai Transfer Done Manually? Draft pembayaran yang belum diajukan akan dibatalkan.`)) return
    setIsMarkingManual(true)
    setApiMessage('')
    setSuccessMessage('')
    try {
      const response = await ConsignorSettlementsApiV2.bulkMarkTransferredManually(ids, note.trim())
      await loadRows(page, filters)
      setSuccessMessage(response.data?.message || 'Settlement manual tersimpan.')
    } catch (error) {
      setApiMessage(error?.response?.data?.messages?.base || error?.response?.data?.message || error.message)
    } finally {
      setIsMarkingManual(false)
    }
  }

  const handleSync = async () => {
    const confirmed = window.confirm(
      'Mulai sinkronisasi BKR dan BKO sekarang untuk semua status Deal?'
    )

    if (!confirmed) return

    setIsSyncing(true)
    setApiMessage('')
    setSuccessMessage('')

    try {
      const response = await ConsignorSettlementsApiV2.syncEligible({
        sku_prefixes : [ 'BKR', 'BKO' ],
        dry_run      : false
      })
      const result = response.data?.data || {}

      await loadRows(page, filters)
      setSuccessMessage(result.queued
        ? response.data?.message || 'Sinkronisasi BKR/BKO dimulai. Refresh daftar untuk melihat hasil.'
        : `Sinkronisasi selesai. Dibuat ${result.created || 0}; sudah ada ${result.already_exists || 0}.`)
    }
    catch (error) {
      setApiMessage(
        error?.response?.data?.message ||
        error?.message ||
        'Sinkronisasi settlement gagal.'
      )
    }
    finally {
      setIsSyncing(false)
    }
  }

  const handleExport = async () => {
    if (!canExport) return

    setIsExporting(true)
    setApiMessage('')

    try {
      const response = await ConsignorSettlementsApiV2.exportCsv({
        ...filters,
        internal_category: fixedInternalCategory || filters.internal_category || undefined,
        finance_transfers: financeScopedView || undefined,
        transfer_status: selectedTransferStatus,
        requested_from: financeArea && !financeScopedView ? undefined : filters.requested_from,
        requested_to: financeArea && !financeScopedView ? undefined : filters.requested_to,
        deal_created_from: financeScopedView ? undefined : filters.deal_created_from,
        deal_created_to: financeScopedView ? undefined : filters.deal_created_to,
        settlement_kind: settlementKind
      })

      downloadBlob(
        response.data,
        `${sellerMode ? 'seller' : 'consignor'}_settlements_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`
      )
    }
    catch (error) {
      let payload = error?.response?.data
      if (payload instanceof Blob) {
        try { payload = JSON.parse(await payload.text()) }
        catch { payload = null }
      }
      setApiMessage(
        payload?.messages?.base || payload?.message ||
        error?.message ||
        'Export CSV gagal.'
      )
    }
    finally {
      setIsExporting(false)
    }
  }

  const openDetail = (row) => {
    if (row.pic_warning_required === true) {
      const pic = String((row.settlement_kind === 'seller' ? row.sourced_by : row.product_curator_pic) || '').trim()
      if (!window.confirm(`Apakah kamu yakin mau buka Settlement Detail PIC ${!pic || pic === '-' ? 'yang belum di-assign' : pic}? Buying price milik PIC lain tetap disembunyikan.`)) return
    }
    const returnTo = `${window.location.pathname}${window.location.search}`

    history.push(
      `${detailBasePath}${financeScopedView ? '/transfer' : ''}/${row.id}?return_to=${encodeURIComponent(returnTo)}`,
      { settlementPicConfirmedId: String(row.id) }
    )
  }

  const stopRowClick = (event) => {
    event.stopPropagation()
  }

  const currentPage = Number(meta.current_page || page || 1)
  const totalPages = Number(meta.total_pages || 1)
  const totalCount = Number(meta.total_count || 0)
  const perPage = Number(meta.per_page || 20)
  const loadedCount = rows.length
  const loadedFrom = loadedCount > 0
    ? ((currentPage - 1) * perPage) + 1
    : 0
  const loadedTo = loadedCount > 0
    ? loadedFrom + loadedCount - 1
    : 0

  const loadedRangeLabel = loadedCount > 0
    ? `Data ${loadedFrom}-${loadedTo} dari ${totalCount}`
    : `Total ${totalCount} data`
  const sellerMode = settlementKind === 'seller'
  const pcConsignorView = canNegotiate && ['consignor', 'seller'].includes(settlementKind)
  const useBusinessStateLabels = true
  const displayStateLabel = (state) => (
    (useBusinessStateLabels ? STATE_LABELS : LEGACY_STATE_LABELS)[state] || state || '-'
  )
  const moduleTitle = sellerMode
    ? 'Seller Settlement'
    : settlementKind === 'all'
      ? 'Consignor & Seller Settlement'
      : 'Consignor Settlement'
  const counterpartyLabel = sellerMode ? 'Seller' : 'Consignor'
  const showProductCurator = !sellerMode

  return (
    <>
      { !embedded && (
        <TitlePage
          mainTitle={ pageTitle || (financeArea ? 'Transfer Request' : moduleTitle) }
          subTitle={ pageSubTitle || teamLabel || 'Ready for Settlement' }
        />
      ) }

      { !embedded && (
      <Alert color='info'>
        { financeApproval
          ? `Finance Approval ${fixedInternalCategory || ''}: review request, data rekening, dan nominal settlement sebelum menjadi Ready to Transfer.`
          : financeProcess
            ? 'Transfer Process: hanya menampilkan request yang sudah di-approve dan siap diproses transfer.'
            : canNegotiate
              ? sellerMode
                ? <><strong>Buying Team — Seller Settlement SKU BKO:</strong><ol className='mb-0 pl-4'><li>Pilih email seller dan konfirmasi rekening bank.</li><li>Isi harga settlement dan simpan draft pembayaran.</li><li>Sejak Deal Sourced, ajukan Approval to Finance; tidak perlu menunggu barang tiba atau Deal Completed.</li></ol></>
                : <><strong>Product Curator — Consignor Settlement SKU BKR:</strong><ol className='mb-0 pl-4'><li>Isi Final Buying Price, lengkapi negosiasi dan bukti pendukung.</li><li>Pilih email consignor dan konfirmasi rekening bank.</li><li>Isi nominal settlement dan simpan draft pembayaran.</li><li>Setelah Deal Completed, ajukan Approval to Finance.</li></ol></>
              : 'Finance: klik kartu/baris untuk melihat detail, upload bukti transfer, tandai Sudah Ditransfer, lalu kirim/kirim ulang email ke consignor atau seller.' }
      </Alert>
      ) }

      { apiMessage && (
        <Alert color='warning'>
          { apiMessage }
        </Alert>
      ) }

      { successMessage && (
        <Alert color='success'>
          { successMessage }
        </Alert>
      ) }

      <Card className='mb-3'>
        <CardHeader>
          <Row className='align-items-center'>
            <Col md={ 6 } className='mb-3 mb-md-0' style={{ minWidth: 0 }}>
              <h3 className='mb-1'>
                { financeApproval
                  ? pageSubTitle || 'Finance Approval'
                  : financeProcess
                    ? 'Transfer Process'
                    : financeQueue
                      ? 'Antrian Settlement Finance'
                      : sellerMode
                        ? 'Ready for Settlement'
                        : 'Siap Settlement' }
              </h3>
              <div className='text-muted small'>
                { financeApproval
                  ? 'Request menunggu Finance Approval. Klik baris untuk review detail sebelum approve / return.'
                  : financeProcess
                    ? 'Ready to Transfer. Filter tanggal berdasarkan Request Date.'
                    : financeQueue
                      ? 'Hanya menampilkan settlement yang sudah diajukan ke Finance, sudah ditransfer, email gagal, atau email sudah terkirim.'
                      : sellerMode
                        ? 'SKU BKO / Group BKO pada semua Deal status; filter tanggal berdasarkan Deal Created. Data baru disinkronkan otomatis setiap 10 menit.'
                        : 'SKU BKR dengan consignment pada semua Deal status; filter tanggal berdasarkan Deal Created. Data baru disinkronkan otomatis setiap 10 menit.' }
              </div>
            </Col>

            <Col md={ 6 } className='d-flex flex-wrap justify-content-md-end align-items-center' style={{ gap: '0.5rem', minWidth: 0 }}>
              { canNegotiate && settlementKind === 'consignor' && !financeArea && (
                <Button
                  color='warning'
                  outline
                  style={{ maxWidth: '100%', whiteSpace: 'normal' }}
                  disabled={ isLoading || !filterOptions.current_admin_product_curator }
                  title={ filterOptions.current_admin_product_curator ? `PC: ${filterOptions.current_admin_product_curator}` : 'Email akun belum mempunyai mapping PIC Curator.' }
                  onClick={ applyOwnPendingEmail }
                >
                  Transfer Done Belum di-email
                </Button>
              ) }
              { canExport && (
              <Button
                color='secondary'
                outline
                style={{ maxWidth: '100%', whiteSpace: 'normal' }}
                disabled={ isExporting }
                onClick={ handleExport }
              >
                { isExporting ? 'Sedang export...' : 'Export CSV' }
              </Button>
              ) }

              { canSync && (
                <Button
                  color='primary'
                  style={{ maxWidth: '100%', whiteSpace: 'normal' }}
                  disabled={ isSyncing }
                  onClick={ handleSync }
                >
                  { isSyncing
                    ? 'Sedang sinkronisasi...'
                    : sellerMode
                      ? 'Sinkronisasi Sekarang'
                      : 'Sinkronisasi Sekarang' }
                </Button>
              ) }
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Row form>
            { !fixedInternalCategory && <Col md={ 2 } className={ pcConsignorView || financeArea ? 'd-md-none' : '' }><Label>Internal Category</Label><Input type='select' aria-label='Internal Category' value={ filters.internal_category } onChange={ (event) => applyColumnFilter('internal_category', event.target.value) }><option value=''>All</option>{ ['H', 'C', 'W'].map((value) => <option key={ value }>{ value }</option>) }</Input></Col> }

            <Col md={ 3 } className={ pcConsignorView || financeArea ? 'd-md-none' : '' }>
              <Label>Status</Label><Input
                  type='select'
                  value={ filters.state }
                  onChange={ (event) => applyColumnFilter('state', event.target.value) }
                >
                  <option value=''>All Statuses</option>
                  { financeQueue && (
                    <option value='finance_requested,transferred,email_failed,email_sent'>
                      Antrian Finance
                    </option>
                  ) }
                  { settlementStatusOptions(filterOptions.states || [], displayStateLabel).map((option) => (
                    <option key={ option.value } value={ option.value }>
                      { option.label }
                    </option>
                  )) }
                </Input>
            </Col>
            <Col md={ 3 }>
              <FormGroup>
                <Label>Search</Label>
                <Input
                  value={ filters.q }
                  onChange={ (event) => setFilter('q', event.target.value) }
                  placeholder={ sellerMode ? 'Search SKU, deal, product, or seller' : 'Search SKU, deal, product, or consignor' }
                />
              </FormGroup>
            </Col>

            <Col md={ 1 }>
              <FormGroup>
                <Label>{ financeScopedView ? 'Request From' : financeArea || pcConsignorView ? 'Deal From' : 'From' }</Label>
                <Input
                  type='date'
                  value={ financeScopedView ? filters.requested_from : pcConsignorView || financeArea ? filters.deal_created_from : filters.eligible_from }
                  onChange={ (event) => setFilter(financeScopedView ? 'requested_from' : pcConsignorView || financeArea ? 'deal_created_from' : 'eligible_from', event.target.value) }
                />
              </FormGroup>
            </Col>

            <Col md={ 1 }>
              <FormGroup>
                <Label>{ financeScopedView ? 'Request To' : financeArea || pcConsignorView ? 'Deal To' : 'To' }</Label>
                <Input
                  type='date'
                  value={ financeScopedView ? filters.requested_to : pcConsignorView || financeArea ? filters.deal_created_to : filters.eligible_to }
                  onChange={ (event) => setFilter(financeScopedView ? 'requested_to' : pcConsignorView || financeArea ? 'deal_created_to' : 'eligible_to', event.target.value) }
                />
              </FormGroup>
            </Col>

            <Col md={ 1 } className='d-flex align-items-end'>
              <Button
                color='success'
                className='mb-3'
                onClick={ () => loadRows(1, filters) }
              >
                Apply Filter
              </Button>
            </Col>
          </Row>

          { rows.some((row) => row.can_view_buying_price === false) && <Alert color='info'>Buying price dibatasi sesuai PIC Product Curator (BKR) atau PIC Sourced By pada Deal (BKO). Settlement PIC lain tetap bisa dibuka untuk backup setelah konfirmasi; harga ditampilkan sebagai “-”.</Alert> }
          <div className='d-flex justify-content-between mb-2'>
            <small className='text-muted'>
              { `Total ${meta.total_count || 0} settlement` }
            </small>

            <small className='text-muted'>
              { `Halaman ${meta.current_page || 1} / ${meta.total_pages || 1}` }
            </small>
          </div>

          { canNegotiate && !financeArea && <Button className='mb-3' color='secondary' outline disabled={ isMarkingManual } aria-expanded={ showManualTransfers } onClick={ () => { setShowManualTransfers(!showManualTransfers); setSelectedIds([]) } }>{ showManualTransfers ? 'Hide Bulk TF Manually' : 'Show Bulk TF Manually' }</Button> }
          <Alert color='info'>
            <ul className='mb-0 pl-4'>
              <li><strong>On Process:</strong> lengkapi data, dokumen dan nominal pembayaran, lalu klik Approval to Finance.</li>
              <li><strong>Finance Approval:</strong> menunggu review dan persetujuan Finance.</li>
              <li><strong>Ready to Transfer:</strong> pembayaran disetujui Finance; menunggu transfer dan upload slip.</li>
              <li><strong>Transfer Done:</strong> uang sudah ditransfer; { sellerMode ? 'pembayaran seller selesai.' : 'PC perlu mengirim email bukti transfer per pembayaran.' }</li>
              <li><strong>Transfer Done Manually:</strong> dicatat lunas di luar sistem; tidak mengirim uang atau email.</li>
              { !sellerMode && <>
                <li><strong>Email Not Sent:</strong> email bukti transfer belum dikirim; PC klik Send Email pada pembayaran yang sudah ditransfer.</li>
                <li><strong>Email Submitted / Processed:</strong> email sudah diajukan; menunggu konfirmasi pengiriman dari SendGrid.</li>
                <li><strong>Email Failed:</strong> pengiriman email gagal; periksa penyebabnya lalu kirim ulang setelah diperbaiki.</li>
                <li><strong>Email Delivered:</strong> server email penerima sudah menerima email bukti transfer.</li>
              </> }
              <li><strong>Cancelled:</strong> settlement dibatalkan; tidak diproses lebih lanjut.</li>
            </ul>
            { !sellerMode && <div className='mt-2'><strong>Counter di menu:</strong> jumlah settlement dengan transfer yang sudah selesai, namun email bukti transfer belum dikirim. Email yang gagal dan perlu dikirim ulang juga masuk counter; satu settlement dihitung satu kali. Detail consignor yang namanya mengandung seller (tanpa membedakan huruf besar/kecil) dikecualikan dari counter; detail lain pada akun yang sama tetap mengikuti aturan biasa.</div> }
          </Alert>
          { manualSelectionEnabled && (
            <Alert color='warning'>
              <div className='mb-2'>Pilih settlement yang sudah lunas di luar sistem untuk Deal sebelum 15 Oktober 2026 pukul 00.00 WIB. Pilihan hanya berlaku pada halaman ini; settlement dalam proses Finance / sudah dibayar tidak dapat dipilih.</div>
              <Button color='warning' disabled={ isLoading || isMarkingManual || selectedIds.length === 0 } onClick={ markSelectedManually }>
                { `Bulk Mark as Transfer Done Manually (${selectedIds.length})` }
              </Button>
              <Button className='ml-2' outline disabled={ isLoading || isMarkingManual } onClick={ () => loadRows(page, filters) }>Refresh</Button>
            </Alert>
          ) }
          <div className='d-none d-md-block' style={{ overflowX: 'auto' }}>
            <Table
              bordered
              striped
              hover
              size='sm'
              style={{
                tableLayout : 'auto',
                width       : '100%',
                minWidth    : financeArea ? '2350px' : pcConsignorView ? '1850px' : '1550px',
                fontSize    : '0.82rem'
              }}
            >
              <thead className='thead-light text-center'>
                { pcConsignorView || financeArea ? (
                  <tr>
                    { manualSelectionEnabled && <th><input type='checkbox' aria-label='Select all eligible settlements on this page' disabled={ isLoading || isMarkingManual || !eligibleIds.length } checked={ eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.includes(id)) } onChange={ (event) => setSelectedIds(event.target.checked ? eligibleIds : []) } /></th> }
                    <SettlementColumnHeader width={ 145 } label={ <>Transaction<br />Date</> } />
                    <SettlementColumnHeader width={ 145 } label='Settlement Ref'>
                      <Input bsSize='sm' aria-label='Search settlement number' placeholder='BNKST-1234567' value={ filters.settlement_number } onChange={ (event) => setFilter('settlement_number', normalizeSettlementNumberInput(event.target.value)) } onKeyDown={ (event) => { if (event.key === 'Enter') loadRows(1, filters) } } onBlur={ () => loadRows(1, filters) } />
                    </SettlementColumnHeader>
                    { financeArea && <SettlementColumnHeader width={ 105 } label='Transfer ID' /> }
                    <SettlementColumnHeader width={ 130 } label={ <>Deal<br />Status</> }>
                      <Input bsSize='sm' type='select' aria-label='Deal Status' value={ filters.deal_state } onChange={ (event) => applyColumnFilter('deal_state', event.target.value) }>
                        <option value=''>All</option>
                        { (filterOptions.deal_states || []).map((state) => <option key={ state } value={ state }>{ state }</option>) }
                      </Input>
                    </SettlementColumnHeader>
                    <SettlementColumnHeader width={ 215 } label='SKU' />
                    <SettlementColumnHeader width={ 245 } label='Nama Product' />
                    <SettlementColumnHeader width={ 120 } label='Internal Category'>
                      { !fixedInternalCategory && (
                        <Input bsSize='sm' type='select' aria-label='Internal Category' value={ filters.internal_category } onChange={ (event) => applyColumnFilter('internal_category', event.target.value) }>
                          <option value=''>All</option>
                          { ['H', 'C', 'W'].map((value) => <option key={ value } value={ value }>{ value }</option>) }
                        </Input>
                      ) }
                    </SettlementColumnHeader>
                    <SettlementColumnHeader width={ 180 } label={ financeArea ? 'Nama Consignor / Seller' : sellerMode ? 'Nama Seller' : 'Nama Consignor' } />
                    <SettlementColumnHeader width={ financeArea ? 165 : 135 } label={ financeArea ? 'Nama PC / Sourced By' : sellerMode ? 'Sourced By' : 'Nama PC' }>
                      { financeArea ? <>
                        <Input bsSize='sm' type='select' aria-label='Product Curator' value={ filters.product_curator } onChange={ (event) => applyColumnFilter('product_curator', event.target.value) }>
                          <option value=''>All PC</option>
                          { (filterOptions.product_curators || []).map((name) => <option key={ name } value={ name }>{ name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() }</option>) }
                        </Input>
                        <Input bsSize='sm' type='select' className='mt-1' aria-label='Sourced By' value={ filters.sourced_by } onChange={ (event) => applyColumnFilter('sourced_by', event.target.value) }>
                          <option value=''>All Sourced By</option>
                          { (filterOptions.sourced_by || []).map((name) => <option key={ name } value={ name }>{ name }</option>) }
                        </Input>
                      </> : <Input bsSize='sm' type='select' aria-label={ sellerMode ? 'Sourced By' : 'Product Curator' } value={ sellerMode ? filters.sourced_by : filters.product_curator } onChange={ (event) => applyColumnFilter(sellerMode ? 'sourced_by' : 'product_curator', event.target.value) }>
                        <option value=''>All</option>
                        { ((sellerMode ? filterOptions.sourced_by : filterOptions.product_curators) || []).map((name) => <option key={ name } value={ name }>{ sellerMode ? name : name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() }</option>) }
                      </Input> }
                    </SettlementColumnHeader>
                    <SettlementColumnHeader width={ 140 } label={ <>Buying<br />Price</> } />
                    <SettlementColumnHeader width={ 150 } label={ <>Final Buying<br />Price</> } />
                    <SettlementColumnHeader width={ 140 } label={ <>Selling<br />Price</> } />
                    <SettlementColumnHeader width={ 165 } label={ <>Settlement<br />Status</> }>
                      <Input bsSize='sm' type='select' aria-label='Settlement Status' value={ filters.state } onChange={ (event) => applyColumnFilter('state', event.target.value) }>
                        <option value=''>All</option>
                        { settlementStatusOptions(filterOptions.states || [], displayStateLabel).map((option) => <option key={ option.value } value={ option.value }>{ option.label }</option>) }
                      </Input>
                    </SettlementColumnHeader>
                    { financeArea && <><SettlementColumnHeader width={ 160 } label='Request Date' /><SettlementColumnHeader width={ 160 } label='Payment Amount (IDR)' /><SettlementColumnHeader width={ 155 } label='Bank' /><SettlementColumnHeader width={ 175 } label='Account Name' /><SettlementColumnHeader width={ 160 } label='Account Number' /></> }
                    { !sellerMode && <SettlementColumnHeader width={ 130 } label='Email Status'>
                      { canNegotiate && settlementKind === 'consignor' && <Input bsSize='sm' type='select' aria-label='Email Status' value={ filters.email_status } onChange={ (event) => applyColumnFilter('email_status', event.target.value) }><option value=''>All</option>{ ['not_sent', 'not_mandatory_sent', 'submitted', 'delivered', 'failed', 'unknown'].map((value) => <option key={ value } value={ value }>{ value }</option>) }</Input> }
                    </SettlementColumnHeader> }
                  </tr>
                ) : sellerMode ? (
                  <tr>
                    { manualSelectionEnabled && <th><input type='checkbox' aria-label='Select all eligible settlements on this page' disabled={ isLoading || isMarkingManual || !eligibleIds.length } checked={ eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.includes(id)) } onChange={ (event) => setSelectedIds(event.target.checked ? eligibleIds : []) } /></th> }
                    <th>Order Number</th>
                    <th>Deal Status</th>
                    <th>SA</th>
                    <th>Customer</th>
                    <th>SKU</th>
                    <th>Product Name</th>
                    <th className='text-right'>Buying Price</th>
                    <th className='text-right'>DP</th>
                    <th className='text-right'>Selling Price</th>
                    <th>Sourced By</th>
                    <th>Settlement Status</th>
                  </tr>
                ) : (
                  <tr>
                    { manualSelectionEnabled && <th><input type='checkbox' aria-label='Select all eligible settlements on this page' disabled={ isLoading || isMarkingManual || !eligibleIds.length } checked={ eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.includes(id)) } onChange={ (event) => setSelectedIds(event.target.checked ? eligibleIds : []) } /></th> }
                    <th style={{ width: '9%' }}>Deal</th>
                    <th style={{ width: '13%' }}>SKU</th>
                    <th style={{ width: '22%' }}>Product</th>
                    <th style={{ width: '15%' }}>{ counterpartyLabel }</th>
                    <th style={{ width: '15%' }}>Product Curator</th>
                    <th style={{ width: '9%' }} className='text-right'>Buying Price</th>
                    <th style={{ width: '9%' }} className='text-right'>Final Buying Price</th>
                    <th style={{ width: '8%' }}>Status</th>
                  </tr>
                ) }
              </thead>

              <tbody>
                { rows.map((row) => (
                  <tr
                    key={ row.id }
                    onClick={ () => openDetail(row) }
                    style={{ cursor: 'pointer' }}
                    title='Klik baris untuk membuka Detail Settlement'
                  >
                    { manualSelectionEnabled && <td onClick={ stopRowClick }><input type='checkbox' aria-label={ `Select settlement ${row.settlement_number || row.id}` } disabled={ isLoading || isMarkingManual || !row.can_mark_transferred_manually } checked={ selectedIds.includes(row.id) } onChange={ () => toggleManualSelection(row.id) } /></td> }
                    { pcConsignorView || financeArea ? (
                      <>
                        <td style={ singleLineCellStyle }>{ formatDateTime(row.transaction_date || row.deal_created_at) }</td>
                        <td style={ compactCellStyle }>
                          <a
                            href={ `${detailBasePath}${financeScopedView ? '/transfer' : ''}/${row.id}?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}` }
                            onClick={ stopRowClick }
                            target='_blank'
                            rel='noopener noreferrer'
                            title='Buka Detail Settlement'
                          >
                            { row.settlement_number || '-' }
                          </a>
                        </td>
                        { financeArea && <td style={ singleLineCellStyle }>{ row.transfer_id || '-' }</td> }
                        <td style={ singleLineCellStyle }>{ row.deal_state === 'swap_without_deal' ? 'Swap — tanpa Deal' : row.deal_state || row.eligible_deal_state || '-' }{ row.swap_note && <small className='d-block' style={{ whiteSpace: 'normal' }}>Note: {row.swap_note}</small> }</td>
                        <td style={ singleLineCellStyle }>
                          { row.inventory_id ? (
                            <a
                              href={ `/app/warehouse-team/inventory/${row.inventory_id}` }
                              onClick={ stopRowClick }
                              target='_blank'
                              rel='noopener noreferrer'
                              title='Buka Inventory Detail'
                            >
                              { row.sku || '-' }
                            </a>
                          ) : (
                            row.sku || '-'
                          ) }
                        </td>
                        <td style={ cellStyle } title={ row.product_name || '' }>
                          <div>{ row.product_name || '-' }</div>
                        </td><td>{ row.internal_category || '-' }</td>
                        <td style={ singleLineCellStyle }>{ row.counterparty_user_detail_path ? <a href={ row.counterparty_user_detail_path } onClick={ stopRowClick } target='_blank' rel='noopener noreferrer'>{ row.counterparty_name || row.consignor_name || '-' }</a> : row.counterparty_name || row.consignor_name || '-' }</td>
                        <td style={ cellStyle }>{ ((sellerMode || financeArea && row.settlement_kind === 'seller') ? row.sourced_by : row.product_curator_pic) || '-' }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.current_product_buying_price) }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.settlement_buying_price) }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.selling_price) }</td>
                        <td style={ compactCellStyle }>
                          <Badge color={ stateColor(row.state) } style={{ fontSize: '0.7rem', lineHeight: 1.35, padding: '0.4rem 0.6rem' }}>
                            { displayStateLabel(row.state) }
                          </Badge>
                          <small className='d-block text-muted'>{ settlementStatusNote(row) }</small>
                        </td>
                        { financeArea && <><td style={ singleLineCellStyle }>{ formatDateTime(row.finance_confirmation_requested_at) }</td><td style={ compactCellStyle } className='text-right'>{ formatMoney(row.requested_payment_amount_idr) }</td><td style={ cellStyle }>{ row.bank_name || '-' }</td><td style={ cellStyle }>{ row.bank_account_holder_name || '-' }</td><td style={ singleLineCellStyle }>{ row.bank_account_number || '-' }</td></> }
                        { !sellerMode && <td>{ settlementEmailLabel(row) }</td> }
                      </>
                    ) : sellerMode ? (
                      <>
                        <td style={ compactCellStyle }>{ row.reference_number || '-' }</td>
                        <td style={ compactCellStyle }>{ row.deal_state || '-' }</td>
                        <td style={ cellStyle }>{ row.sales_assistant || '-' }</td>
                        <td style={ cellStyle }>{ row.customer_name || '-' }</td>
                        <td style={ compactCellStyle }>{ row.sku || '-' }</td>
                        <td style={ cellStyle }>{ row.product_name || '-' }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.original_buying_price) }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.down_payment) }</td>
                        <td style={ compactCellStyle } className='text-right'>{ formatMoney(row.selling_price) }</td>
                        <td style={ cellStyle }>{ row.sourced_by || '-' }</td>
                        <td style={ compactCellStyle }><Badge color={ stateColor(row.state) } title={ settlementStatusNote(row) }>{ displayStateLabel(row.state) }</Badge><small className='d-block text-muted'>{ settlementStatusNote(row) }</small></td>
                      </>
                    ) : (
                      <>
                        <td style={ compactCellStyle }>
                          <a
                            href={ row.deal_id ? `/app/sales/deals/${row.deal_id}` : undefined }
                            onClick={ stopRowClick }
                            target='_blank'
                            rel='noopener noreferrer'
                            title='Buka Detail Deal'
                          >
                            { row.reference_number || '-' }
                          </a>
                        </td>
                        <td style={ compactCellStyle }>
                          { row.inventory_id ? (
                            <a
                              href={ `/app/warehouse-team/inventory/${row.inventory_id}` }
                              onClick={ stopRowClick }
                              target='_blank'
                              rel='noopener noreferrer'
                              title='Buka Inventory Detail'
                            >
                              { row.sku || '-' }
                            </a>
                          ) : (
                            row.sku || '-'
                          ) }
                        </td>
                        <td style={ cellStyle } title={ row.product_name || '' }>
                          <div>
                            { row.product_name || '-' }
                          </div>
                        </td><td>{ row.internal_category || '-' }</td>
                        <td style={ cellStyle }>
                          <div>
                            { row.counterparty_name || row.consignor_name || '-' }
                          </div>
                          <small className='text-muted d-block' style={{ wordBreak: 'break-word' }}>
                            { row.counterparty_reference_email || row.consignor_email || '-' }
                          </small>
                        </td>
                        <td style={ cellStyle }>
                          <div>{ (sellerMode ? row.sourced_by : row.product_curator_pic) || '-' }</div>
                        </td>
                        <td style={ compactCellStyle } className='text-right'>
                          { formatMoney(row.original_buying_price) }
                        </td>
                        <td style={ compactCellStyle } className='text-right'>
                          { formatMoney(row.settlement_buying_price) }
                        </td>
                        <td style={ compactCellStyle }>
                          <Badge color={ stateColor(row.state) } title={ settlementStatusNote(row) }>
                            { displayStateLabel(row.state) }
                          </Badge>
                          <small className='d-block text-muted'>{ settlementStatusNote(row) }</small>
                        </td>
                      </>
                    ) }
                  </tr>
                )) }

                { !isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={ (financeArea ? 19 : pcConsignorView ? (sellerMode ? 12 : 13) : sellerMode ? 11 : 8) + (manualSelectionEnabled ? 1 : 0) } className='text-center text-muted py-4'>
                      No settlement data.
                    </td>
                  </tr>
                ) }

                { isLoading && (
                  <tr>
                    <td colSpan={ (financeArea ? 19 : pcConsignorView ? (sellerMode ? 12 : 13) : sellerMode ? 11 : 8) + (manualSelectionEnabled ? 1 : 0) } className='text-center text-muted py-4'>
                      Loading...
                    </td>
                  </tr>
                ) }
              </tbody>
            </Table>
          </div>

          <div className='d-md-none'>
            { isLoading && (
              <div className='text-center text-muted py-4'>
                Loading...
              </div>
            ) }

            { !isLoading && rows.length === 0 && (
              <div className='text-center text-muted py-4'>
                No settlement data.
              </div>
            ) }

            { !isLoading && rows.map((row) => (
              <Card
                key={ row.id }
                className='mb-2'
                onClick={ () => openDetail(row) }
                style={{ cursor: 'pointer' }}
              >
                <CardBody className='py-3'>
                  { manualSelectionEnabled && <label onClick={ stopRowClick }><input type='checkbox' disabled={ isLoading || isMarkingManual || !row.can_mark_transferred_manually } checked={ selectedIds.includes(row.id) } onChange={ () => toggleManualSelection(row.id) } /> Pilih settlement manual</label> }
                  <div
                    className='font-weight-bold'
                    style={{
                      lineHeight : 1.35,
                      whiteSpace : 'normal',
                      wordBreak  : 'break-word'
                    }}
                  >
                    { row.inventory_id ? (
                      <a
                        href={ `/app/warehouse-team/inventory/${row.inventory_id}` }
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={ stopRowClick }
                        title='Buka Detail Inventory'
                      >
                        { row.sku || '-' }
                      </a>
                    ) : (
                      row.sku || '-'
                    ) }
                  </div>

                  <div
                    className='mt-1'
                    style={{
                      fontSize   : '1rem',
                      lineHeight : 1.4,
                      whiteSpace : 'normal',
                      wordBreak  : 'break-word'
                    }}
                  >
                    { row.product_name || '-' }
                  </div>

                  <small className='text-muted d-block'>
                    { row.internal_category || '-' }
                  </small>

                  <div className='mt-2 mb-2'>
                    <Badge
                      color={ stateColor(row.state) }
                      style={{
                        fontSize   : '0.68rem',
                        lineHeight : 1.2,
                        whiteSpace : 'normal',
                        textAlign  : 'center'
                      }}
                    >
                      { displayStateLabel(row.state) }
                    </Badge>
                  </div>

                  { financeScopedView && (
                    <div className='mb-2'>
                      <small className='text-muted d-block'>Request Date</small>
                      <strong>{ formatDateTime(row.finance_confirmation_requested_at) }</strong><div>Transfer ID: { row.transfer_id }</div><div>Amount IDR: { formatMoney(row.requested_payment_amount_idr) }</div>
                      <div className='mt-2'>{ `${row.bank_name || '-'} • ${row.bank_account_holder_name || '-'}` }</div>
                      <small className='text-muted d-block'>{ `${row.bank_account_number || '-'} • ${row.bank_branch || '-'}` }</small>
                    </div>
                  ) }

                  { pcConsignorView && (
                    <div className='mb-2'>
                      <small className='text-muted d-block'>Transaction Date</small>
                      <strong>{ formatDateTime(row.transaction_date || row.deal_created_at) }</strong>
                    </div>
                  ) }

                  <div className='mb-2'><strong>Settlement:</strong> { row.settlement_number || '-' }</div>
                  <div className='mb-2'>
                    <strong>Deal:</strong>{' '}
                    <a
                      href={ row.deal_id ? `/app/sales/deals/${row.deal_id}` : undefined }
                      onClick={ stopRowClick }
                      target='_blank'
                      rel='noopener noreferrer'
                      title='Buka Detail Deal'
                    >
                      { row.reference_number || '-' }
                    </a>
                    { pcConsignorView && (
                      <><span className='text-muted'>{ ` • ${row.deal_state || row.eligible_deal_state || '-'}` }</span>{row.swap_note && <small className='d-block'>Note: {row.swap_note}</small>}</>
                    ) }
                  </div>

                  <div>
                    <strong>{ counterpartyLabel }:</strong> { row.counterparty_user_detail_path ? <a href={ row.counterparty_user_detail_path } onClick={ stopRowClick }>{ row.counterparty_name || row.consignor_name || '-' }</a> : row.counterparty_name || row.consignor_name || '-' }
                  </div>
                  <div className='mt-2'>
                    <strong>{ sellerMode ? 'Sourced By:' : 'Product Curator:' }</strong> { (sellerMode ? row.sourced_by : row.product_curator_pic) || '-' }
                  </div>

                  { !sellerMode && <div className='mt-2'><strong>Email Status:</strong> { settlementEmailLabel(row) }</div> }

                  <Row className='mt-3'>
                    <Col xs={ pcConsignorView ? 4 : 6 }>
                      <small className='text-muted d-block'>Buying Price</small>
                      <strong>{ formatMoney(row.current_product_buying_price) }</strong>
                    </Col>
                    <Col xs={ pcConsignorView ? 4 : 6 }>
                      <small className='text-muted d-block'>Final Buying Price</small>
                      <strong>{ formatMoney(row.settlement_buying_price) }</strong>
                    </Col>
                    { pcConsignorView && (
                      <Col xs={ 4 }>
                        <small className='text-muted d-block'>Selling Price</small>
                        <strong>{ formatMoney(row.selling_price) }</strong>
                      </Col>
                    ) }
                  </Row>
                </CardBody>
              </Card>
            )) }
          </div>

          { !isLoading && (
            <>
              <div className='text-center text-muted mt-3 mb-2'>
                <small>
                  { `Memuat ${loadedCount} data · ${loadedRangeLabel} · Halaman ${currentPage} dari ${totalPages}` }
                </small>
              </div>

              { totalPages > 1 && (
                <div className='d-flex justify-content-center mt-2'>
                  <PaginationBar
                    pagination={{
                      ...meta,
                      current_page : currentPage,
                      total_pages  : totalPages,
                      total_count  : totalCount,
                      per_page     : perPage
                    }}
                    onClickPage={ (nextPage) => loadRows(nextPage, filters) }
                  />
                </div>
              ) }
            </>
          ) }
        </CardBody>
      </Card>
    </>
  )
}

ConsignorSettlementsListPage.propTypes = propTypes
ConsignorSettlementsListPage.defaultProps = defaultProps

export default ConsignorSettlementsListPage
